import {
	Button,
	NumberField,
	Picker,
	PickerItem,
	Text,
	TextField,
} from '@react-spectrum/s2';
import { type KeyboardEvent, useState } from 'react';

import { cn } from '@/utils/cn';

import {
	OUTCOME_KEYS,
	OUTCOME_LABEL,
	OUTCOME_OTHER,
	type OutcomeKey,
} from './groundingFields';
import { formatNorthStar, type NorthStar } from './northStar';

// The number the strategy gets judged on, as it appears on the summary.
//
// It is ASKED during the walk, right after the goal question (ADO-1963). This row is where it is
// read back and changed afterwards, so it keeps a picker: the summary is a screen of values to
// correct, not a turn in a conversation.
//
// "North star" is our word, not theirs, so it does not appear in anything they read.

type OutcomeChoice = OutcomeKey | typeof OUTCOME_OTHER;

// React Aria's NumberField resets the typed text when the formatOptions object
// changes (it compares by reference), so the object must stay the same.
const USD_WHOLE_DOLLARS: Intl.NumberFormatOptions = {
	style: 'currency',
	currency: 'USD',
	maximumFractionDigits: 0,
};

export function NorthStarRow({
	northStar,
	goalAnswer,
	unsaved = false,
	onConfirm,
}: {
	northStar: NorthStar | null;
	/** What they said they wanted to grow, used to prefill the own-words box. */
	goalAnswer: string;
	/** The row is showing something the server has not taken yet. */
	unsaved?: boolean;
	onConfirm: (star: NorthStar | null) => void;
}) {
	const [editing, setEditing] = useState(false);
	const [outcome, setOutcome] = useState<OutcomeChoice | null>(null);
	const [target, setTarget] = useState<number | null>(null);
	const [ownWords, setOwnWords] = useState('');

	const open = () => {
		setOutcome(northStar?.outcome ?? null);
		setTarget(northStar?.target ?? null);
		// Their own goal answer, verbatim — never re-worded, never mapped to one of our keys.
		setOwnWords(northStar?.ownWords?.trim() || goalAnswer.trim());
		setEditing(true);
	};

	const close = () => setEditing(false);

	const canSave =
		outcome != null && target != null && Number.isFinite(target) && target > 0;

	const clear = () => {
		onConfirm(null);
		setEditing(false);
	};

	const save = () => {
		if (!canSave) return;
		onConfirm({
			outcome,
			target,
			ownWords: outcome === OUTCOME_OTHER ? ownWords.trim() : '',
		});
		setEditing(false);
	};

	// Escape has to stop here. The review step has no handler of its own, so an untrapped Escape
	// reaches the dock and closes the whole panel — taking the outcome and target with it.
	const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
		if (e.key === 'Escape') {
			e.stopPropagation();
			close();
		}
	};

	// Only true once a value is in the vocabulary the validator understands; anything else is a
	// number they are aiming at, which is a promise we can keep.
	//
	// An unsaved number is judgeable by nobody: the run reads what the server holds, so promising
	// to judge a strategy on a value that never landed is the one claim this row must not make.
	const isJudgeable =
		Boolean(northStar) && northStar?.outcome !== OUTCOME_OTHER && !unsaved;

	// Only worth saying about a number they can still see. Once a withdrawal fails the row already
	// reads "Not added", and warning that nothing is unsaved explains nothing.
	const showUnsaved = unsaved && Boolean(northStar);

	if (!editing) {
		return (
			<button
				type="button"
				data-testid="grounding-northstar-row"
				data-confirmed={northStar ? 'true' : 'false'}
				data-unsaved={showUnsaved ? 'true' : 'false'}
				onClick={open}
				style={{ animationDelay: '90ms' }}
				className="animate-grounding-rise w-full rounded-lg px-2 py-1.5
					text-left hover:bg-(--glass-bg-hover)"
			>
				<span className="block text-xs text-(--text-muted)">
					{isJudgeable
						? "The number we'll judge this on"
						: "The number you're aiming for"}
				</span>
				{/* Same weight as every other row on the summary. This one used to be semibold,
				    which made the number look like a heading rather than one answer among
				    twelve. */}
				<span
					className={cn(
						'block truncate text-sm',
						northStar ? 'text-(--text-primary)' : 'text-(--text-muted) italic',
					)}
				>
					{northStar ? formatNorthStar(northStar) : 'Not added'}
				</span>
				{showUnsaved && (
					<span
						data-testid="northstar-unsaved"
						className="mt-0.5 block text-xs text-red-600"
					>
						We couldn&apos;t save this. Open it and save again.
					</span>
				)}
			</button>
		);
	}

	return (
		<div
			data-testid="grounding-northstar-editor"
			onKeyDown={onKeyDown}
			className="space-y-3 rounded-lg border border-(--glass-border) px-2 py-2"
		>
			<p className="text-xs text-(--text-muted)">
				Pick the one number I should judge this strategy on.
			</p>

			<Picker
				label="What to grow"
				size="M"
				data-testid="northstar-outcome"
				selectedKey={outcome}
				onSelectionChange={(key) => setOutcome(String(key) as OutcomeChoice)}
			>
				{[
					...OUTCOME_KEYS.map((key) => (
						<PickerItem key={key} id={key}>
							{OUTCOME_LABEL[key]}
						</PickerItem>
					)),
					<PickerItem key={OUTCOME_OTHER} id={OUTCOME_OTHER}>
						Something else
					</PickerItem>,
				]}
			</Picker>

			{outcome === OUTCOME_OTHER && (
				<TextField
					label="In your words"
					size="M"
					data-testid="northstar-own-words"
					value={ownWords}
					onChange={setOwnWords}
				/>
			)}

			<NumberField
				label="Target"
				size="M"
				data-testid="northstar-target"
				value={target ?? Number.NaN}
				minValue={1}
				formatOptions={outcome === 'revenue' ? USD_WHOLE_DOLLARS : undefined}
				onChange={(v) => setTarget(Number.isFinite(v) ? v : null)}
			/>

			<div className="flex items-center gap-2">
				<Button variant="secondary" onPress={close}>
					<Text>Cancel</Text>
				</Button>
				{northStar && (
					// Setting a number must be undoable. Without this the first save is permanent,
					// and someone who picked the wrong measure can only overwrite it, never say
					// "actually, don't hold me to a number".
					<Button
						variant="secondary"
						data-testid="northstar-clear"
						onPress={clear}
					>
						<Text>Remove</Text>
					</Button>
				)}
				<div className="ml-auto">
					<Button
						variant="accent"
						isDisabled={!canSave}
						data-testid="northstar-save"
						onPress={save}
					>
						<Text>Save</Text>
					</Button>
				</div>
			</div>
		</div>
	);
}
